<template>
  <div>
    <b-modal v-model="showModal" title="Historial de Importaciones" size="xl" hide-footer @hidden="onClose">
      <div>
        <!-- Filtros -->
        <b-row class="mb-3">
        <b-col md="3">
          <b-form-group label="Usuario ejecutor" label-for="filtro-usuario">
            <b-form-input
              id="filtro-usuario"
              v-model="filtros.usuario"
              placeholder="Email del usuario..."
              @keyup.enter="cargarHistorial"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Dominio (opcional)" label-for="filtro-dominio">
            <v-select
              id="filtro-dominio"
              v-model="filtros.dominio"
              :options="dominiosOptions"
              :disabled="dominiosLoading"
              placeholder="Selecciona un dominio..."
              label="text"                            
            >
              <template v-slot:no-options="{ search, searching }">
                <template v-if="searching">
                  No se encontraron resultados para <em>{{ search }}</em>.
                </template>
                <em v-else style="opacity: 0.5">Escribe para buscar.</em>
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Fecha desde" label-for="filtro-fecha-desde">
            <b-form-input
              id="filtro-fecha-desde"
              v-model="filtros.fecha_desde"
              type="date"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Fecha hasta" label-for="filtro-fecha-hasta">
            <b-form-input
              id="filtro-fecha-hasta"
              v-model="filtros.fecha_hasta"
              type="date"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Estado" label-for="filtro-exitoso">
            <b-form-select
              id="filtro-exitoso"
              v-model="filtros.exitoso"
              :options="estadoOptions"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mb-3">
        <b-col class="d-flex justify-content-end">
          <b-button variant="primary" @click="cargarHistorial" size="sm">
            <b-icon icon="search" class="mr-1"></b-icon>
            Buscar
          </b-button>
          <b-button variant="secondary" @click="limpiarFiltros" size="sm" class="ml-2">
            <b-icon icon="x-circle" class="mr-1"></b-icon>
            Limpiar
          </b-button>
        </b-col>
      </b-row>

      <!-- Tabla de historial -->
      <div v-if="cargando" class="text-center py-4">
        <b-spinner variant="primary" label="Cargando..."></b-spinner>
        <p class="mt-2">Cargando historial...</p>
      </div>
      <div v-else>
        <b-table
          striped
          hover
          small
          responsive
          :items="items"
          :fields="fields"
          class="mb-3"
        >
          <template #cell(exitoso)="{ value }">
            <b-badge v-if="value === 0" variant="danger">Fallido</b-badge>
            <b-badge v-else-if="value === 1" variant="success">Exitoso</b-badge>
            <b-badge v-else-if="value === 2" variant="warning">Parcial</b-badge>
            <b-badge v-else variant="secondary">Desconocido</b-badge>
          </template>
          <template #cell(created_at)="{ value }">
            <div>
              <span>{{ value ? new Date(value).toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '-' }}</span><br>
              <small class="text-muted">{{ value ? new Date(value).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '' }}</small>
            </div>
          </template>
          <template #cell(dominio)="row">
            {{ row.item.escuela_filtrada ? row.item.escuela_filtrada.domain : 'General' }}
          </template>
          <template #cell(resumen)="row">
            <small>
              <span v-b-tooltip.hover title="Usuarios creados">
                <b-icon icon="plus-circle" variant="success"></b-icon> {{ row.item.usuarios_creados }}
              </span>
              <span v-b-tooltip.hover title="Usuarios actualizados" class="ml-2">
                <b-icon icon="pencil" variant="info"></b-icon> {{ row.item.usuarios_actualizados }}
              </span>
              <span v-b-tooltip.hover title="Usuarios sin cambios" class="ml-2">
                <b-icon icon="dash-circle" variant="secondary"></b-icon> {{ row.item.usuarios_sin_cambios }}
              </span>
              <span v-b-tooltip.hover title="Usuarios con errores" class="ml-2">
                <b-icon icon="exclamation-triangle" variant="danger"></b-icon> {{ row.item.errores }}
              </span>
            </small>
          </template>
          <template #cell(estado_reversion)="row">
            <div v-if="row.item.revertida_at">
              <b-badge variant="warning">Revertida</b-badge>
              <br>
              <small class="text-muted">
                {{ formatearFecha(row.item.revertida_at) }}
                <br>
                Por: {{ row.item.revertida_por || 'N/A' }}
              </small>
              <br>
              <small v-if="row.item.total_revertidos !== undefined">
                <b-icon icon="trash" variant="danger"></b-icon>
                {{ row.item.total_revertidos }} eliminados
              </small>
            </div>
            <b-badge v-else variant="secondary">No revertida</b-badge>
          </template>
          <template #cell(acciones)="row">
            <b-button 
              size="sm" 
              variant="info" 
              @click="verDetalles(row.item)"
              v-b-tooltip.hover
              title="Ver detalles de la importación"
              class="mr-1"
            >
              <b-icon icon="eye"></b-icon>
            </b-button>
            <b-button 
              v-if="!row.item.revertida_at && (row.item.exitoso === 1 || row.item.exitoso === 2)"
              size="sm" 
              variant="danger" 
              @click="mostrarConfirmReversion(row.item)"
              v-b-tooltip.hover
              title="Revertir importación (eliminar usuarios)"
            >
              <b-icon icon="arrow-counterclockwise"></b-icon>
            </b-button>
          </template>
        </b-table>

        <!-- Paginación -->
        <b-row>
          <b-col class="d-flex justify-content-between align-items-center">
            <div>
              <small class="text-muted">
                Mostrando {{ paginacion.from || 0 }} - {{ paginacion.to || 0 }} de {{ paginacion.total || 0 }} registros
              </small>
            </div>
            <b-pagination
              v-model="paginacion.current_page"
              :total-rows="paginacion.total"
              :per-page="paginacion.per_page"
              @input="cargarHistorial"
              size="sm"
              align="right"
            />
          </b-col>
        </b-row>
      </div>

      <div class="d-flex justify-content-end mt-3">
        <b-button variant="secondary" @click="cerrar">Cerrar</b-button>
      </div>
    </div>
  </b-modal>

  <!-- Modal de Detalles de Importación -->
  <b-modal v-model="showDetallesModal" title="Detalles de la Importación" size="xl" hide-footer>
    <div v-if="detallesImportacion">
      <!-- Resumen general -->
      <b-alert show variant="info" class="mb-4">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h5 class="mb-1" style="color:#fff">
              <b-icon icon="calendar-event"></b-icon>
              {{ formatearFecha(detallesImportacion.created_at) }}
            </h5>
            <small>Usuario: <strong>{{ detallesImportacion.usuario_ejecutor }}</strong></small>
          </div>
          <div class="text-right">
            <h5 class="mb-1">
              <b-badge v-if="detallesImportacion.exitoso === 0" variant="danger">Fallido</b-badge>
              <b-badge v-else-if="detallesImportacion.exitoso === 1" variant="success">Exitoso</b-badge>
              <b-badge v-else-if="detallesImportacion.exitoso === 2" variant="warning">Parcial</b-badge>
            </h5>
            <small>Total procesados: <strong>{{ detallesImportacion.total_procesados }}</strong></small>
          </div>
        </div>
      </b-alert>

      <!-- Tabs para cada categoría -->
      <b-tabs content-class="mt-3">
        <!-- Tab Usuarios Creados -->
        <b-tab title-item-class="text-success">
          <template #title>
            <b-icon icon="plus-circle" variant="success"></b-icon>
            Creados ({{ detallesImportacion.usuarios_creados || 0 }})
          </template>
          <div v-if="detallesImportacion.detalles && detallesImportacion.detalles.usuarios_creados && detallesImportacion.detalles.usuarios_creados.length > 0">
            <b-list-group>
              <b-list-group-item v-for="(usuario, index) in detallesImportacion.detalles.usuarios_creados" :key="'creado-' + index" class="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{{ usuario.nombre || usuario.name || 'N/A' }}</strong>
                  <br>
                  <small class="text-muted">{{ usuario.email }}</small>
                </div>
                <b-badge variant="success" pill>Creado</b-badge>
              </b-list-group-item>
            </b-list-group>
          </div>
          <b-alert v-else show variant="light" class="text-center">
            No se crearon usuarios en esta importación
          </b-alert>
        </b-tab>

        <!-- Tab Usuarios Actualizados -->
        <b-tab title-item-class="text-info">
          <template #title>
            <b-icon icon="pencil" variant="info"></b-icon>
            Actualizados ({{ detallesImportacion.usuarios_actualizados || 0 }})
          </template>
          <div v-if="detallesImportacion.detalles && detallesImportacion.detalles.usuarios_actualizados && detallesImportacion.detalles.usuarios_actualizados.length > 0">
            <b-list-group>
              <b-list-group-item v-for="(usuario, index) in detallesImportacion.detalles.usuarios_actualizados" :key="'actualizado-' + index">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="flex-grow-1">
                    <h6 class="mb-1">
                      <b-icon icon="pencil" variant="info"></b-icon>
                      {{ usuario.nombre || usuario.name || 'N/A' }}
                    </h6>
                    <small class="text-muted d-block mb-2">{{ usuario.email }}</small>
                    
                    <!-- Mostrar los cambios si existen -->
                    <div v-if="usuario.cambios && usuario.cambios.length > 0" class="mt-2">
                      <small><strong>Cambios realizados:</strong></small>
                      <ul class="mb-0 mt-1" style="font-size: 0.85rem;">
                        <li v-for="(cambio, idx) in usuario.cambios" :key="'cambio-' + idx">
                          <!-- Si es un cambio de grupos -->
                          <template v-if="cambio.campo === 'grupos' && cambio.grupos_agregados">
                            <strong>Grupos agregados:</strong>
                            <span v-for="(grupo, gIdx) in cambio.grupos_agregados" :key="'grupo-' + gIdx">
                              <b-badge variant="success" class="ml-1">{{ grupo.nombre }}</b-badge>
                            </span>
                          </template>
                          <!-- Si es un cambio normal con valor_anterior y valor_nuevo -->
                          <template v-else-if="cambio.valor_anterior !== undefined || cambio.valor_nuevo !== undefined">
                            <strong>{{ cambio.campo }}:</strong> 
                            <span class="text-danger">{{ cambio.valor_anterior || 'N/A' }}</span> 
                            <b-icon icon="arrow-right" font-scale="0.8"></b-icon> 
                            <span class="text-success">{{ cambio.valor_nuevo || 'N/A' }}</span>
                          </template>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <b-badge variant="info" pill>Actualizado</b-badge>
                </div>
              </b-list-group-item>
            </b-list-group>
          </div>
          <b-alert v-else show variant="light" class="text-center">
            No se actualizaron usuarios en esta importación
          </b-alert>
        </b-tab>

        <!-- Tab Usuarios Sin Cambios -->
        <b-tab title-item-class="text-secondary">
          <template #title>
            <b-icon icon="dash-circle" variant="secondary"></b-icon>
            Sin cambios ({{ detallesImportacion.usuarios_sin_cambios || 0 }})
          </template>
          <div v-if="detallesImportacion.detalles && detallesImportacion.detalles.usuarios_sin_cambios && detallesImportacion.detalles.usuarios_sin_cambios.length > 0">
            <b-list-group>
              <b-list-group-item v-for="(usuario, index) in detallesImportacion.detalles.usuarios_sin_cambios" :key="'sin-cambio-' + index" class="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{{ usuario.nombre || usuario.name || 'N/A' }}</strong>
                  <br>
                  <small class="text-muted">{{ usuario.email }}</small>
                </div>
                <b-badge variant="secondary" pill>Sin cambios</b-badge>
              </b-list-group-item>
            </b-list-group>
          </div>
          <b-alert v-else show variant="light" class="text-center">
            No hubo usuarios sin cambios en esta importación
          </b-alert>
        </b-tab>

        <!-- Tab Usuarios con Errores -->
        <b-tab title-item-class="text-danger">
          <template #title>
            <b-icon icon="exclamation-triangle" variant="danger"></b-icon>
            Errores ({{ detallesImportacion.errores || 0 }})
          </template>
          <div v-if="detallesImportacion.detalles && detallesImportacion.detalles.usuarios_con_error && detallesImportacion.detalles.usuarios_con_error.length > 0">
            <b-list-group>
              <b-list-group-item v-for="(error, index) in detallesImportacion.detalles.usuarios_con_error" :key="'error-' + index" variant="danger" class="mb-2">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="flex-grow-1">
                    <h6 class="mb-1">
                      <b-icon icon="x-circle-fill" variant="danger"></b-icon>
                      {{ error.nombre || error.name || 'Usuario sin nombre' }}
                    </h6>
                    <div><small><strong>Email:</strong> {{ error.email || 'N/A' }}</small></div>
                    <div v-if="error.linea"><small><strong>Línea CSV:</strong> {{ error.linea }}</small></div>
                    <div class="mt-2">
                      <b-badge variant="danger">{{ error.tipo_error || 'Error' }}</b-badge>
                      <span class="ml-2">{{ error.motivo || error.error || 'Motivo desconocido' }}</span>
                    </div>
                  </div>
                </div>
              </b-list-group-item>
            </b-list-group>
          </div>
          <b-alert v-else show variant="light" class="text-center">
            No hubo errores en esta importación
          </b-alert>
        </b-tab>

        <!-- Tab Reversión (si existe) -->
        <b-tab v-if="detallesImportacion.revertida_at" title-item-class="text-warning">
          <template #title>
            <b-icon icon="arrow-counterclockwise" variant="warning"></b-icon>
            Reversión
          </template>
          
          <!-- Resumen de la reversión -->
          <b-alert show variant="warning" class="mb-3">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="mb-1">
                  <b-icon icon="calendar-event"></b-icon>
                  Revertida: {{ formatearFecha(detallesImportacion.revertida_at) }}
                </h6>
                <small>Por: <strong>{{ detallesImportacion.revertida_por || 'N/A' }}</strong></small>
              </div>
              <div class="text-right">
                <h5 class="mb-0">
                  <b-badge variant="success">
                    <b-icon icon="trash"></b-icon>
                    {{ detallesImportacion.total_revertidos || 0 }} eliminados
                  </b-badge>
                </h5>
                <small v-if="detallesImportacion.total_no_eliminados > 0" class="text-danger">
                  {{ detallesImportacion.total_no_eliminados }} no eliminados
                </small>
              </div>
            </div>
          </b-alert>

          <!-- Usuarios eliminados exitosamente -->
          <div v-if="getUsuariosEliminados().length > 0" class="mb-4">
            <h6 class="mb-2">
              <b-icon icon="check-circle-fill" variant="success"></b-icon>
              Usuarios eliminados ({{ getUsuariosEliminados().length }})
            </h6>
            <b-list-group>
              <b-list-group-item 
                v-for="(usuario, index) in getUsuariosEliminados()" 
                :key="'eliminado-' + index"
                class="d-flex justify-content-between align-items-center"
              >
                <div>
                  <strong>{{ usuario.nombre || usuario.name || 'N/A' }}</strong>
                  <br>
                  <small class="text-muted">{{ usuario.email }}</small>
                </div>
                <b-badge variant="success" pill>
                  <b-icon icon="trash"></b-icon>
                  Eliminado
                </b-badge>
              </b-list-group-item>
            </b-list-group>
          </div>

          <!-- Usuarios NO eliminados (errores) -->
          <div v-if="getUsuariosNoEliminados().length > 0">
            <h6 class="mb-2">
              <b-icon icon="exclamation-triangle-fill" variant="danger"></b-icon>
              Usuarios NO eliminados ({{ getUsuariosNoEliminados().length }})
            </h6>
            <b-list-group>
              <b-list-group-item 
                v-for="(usuario, index) in getUsuariosNoEliminados()" 
                :key="'no-eliminado-' + index"
                variant="danger"
                class="mb-2"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div class="flex-grow-1">
                    <h6 class="mb-1">
                      <b-icon icon="person-x-fill" variant="danger"></b-icon>
                      {{ usuario.nombre || usuario.name || 'Usuario sin nombre' }}
                    </h6>
                    <div><small><strong>Email:</strong> {{ usuario.email || 'N/A' }}</small></div>
                    <div class="mt-2">
                      <b-badge variant="danger">No eliminado</b-badge>
                      <div class="mt-1">
                        <small><strong>Razón:</strong> {{ usuario.razon || 'No especificada' }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </b-list-group-item>
            </b-list-group>
          </div>

          <!-- Si no hay información de reversión -->
          <b-alert v-if="getUsuariosEliminados().length === 0 && getUsuariosNoEliminados().length === 0" show variant="light" class="text-center">
            No hay información detallada de la reversión
          </b-alert>
        </b-tab>

        <!-- Tab Advertencias -->
        <b-tab title-item-class="text-warning">
          <template #title>
            <b-icon icon="exclamation-circle" variant="warning"></b-icon>
            Advertencias
          </template>
          
          <div v-if="tieneAdvertencias()">
            <!-- Advertencias del preview -->
            <div v-if="detallesImportacion.advertencias_preview">
              <h6 class="mb-3">
                <b-icon icon="info-circle" variant="primary"></b-icon>
                Advertencias de Previsualización
              </h6>
              
              <!-- Advertencias "otras" -->
              <div v-if="detallesImportacion.advertencias_preview.otras && detallesImportacion.advertencias_preview.otras.length > 0" class="mb-3">
                <b-alert show variant="warning" v-for="(advertencia, index) in detallesImportacion.advertencias_preview.otras" :key="'otra-' + index">
                  <b-icon icon="exclamation-triangle"></b-icon>
                  {{ advertencia }}
                </b-alert>
              </div>
              
              <!-- Emails inválidos -->
              <div v-if="detallesImportacion.advertencias_preview.emails_invalidos && detallesImportacion.advertencias_preview.emails_invalidos.total > 0" class="mb-3">
                <b-card header-bg-variant="warning" header-text-variant="white">
                  <template #header>
                    <b-icon icon="envelope-x"></b-icon>
                    Emails inválidos ({{ detallesImportacion.advertencias_preview.emails_invalidos.total }})
                  </template>
                  <b-list-group flush>
                    <b-list-group-item v-for="(email, index) in detallesImportacion.advertencias_preview.emails_invalidos.lista" :key="'email-inv-' + index">
                      {{ email }}
                    </b-list-group-item>
                  </b-list-group>
                </b-card>
              </div>
              
              <!-- Dominios no encontrados -->
              <div v-if="detallesImportacion.advertencias_preview.dominios_no_encontrados && detallesImportacion.advertencias_preview.dominios_no_encontrados.total > 0" class="mb-3">
                <b-card header-bg-variant="warning" header-text-variant="white">
                  <template #header>
                    <b-icon icon="building-x"></b-icon>
                    Dominios no encontrados ({{ detallesImportacion.advertencias_preview.dominios_no_encontrados.total }})
                  </template>
                  <b-list-group flush>
                    <b-list-group-item v-for="(dominio, index) in detallesImportacion.advertencias_preview.dominios_no_encontrados.lista" :key="'dominio-nf-' + index">
                      {{ dominio }}
                    </b-list-group-item>
                  </b-list-group>
                </b-card>
              </div>
            </div>

            <!-- Advertencias del backend -->
            <div v-if="detallesImportacion.advertencias_backend && detallesImportacion.advertencias_backend.length > 0" class="mt-4">
              <h6 class="mb-3">
                <b-icon icon="server" variant="info"></b-icon>
                Advertencias del Servidor
              </h6>
              <b-alert show variant="info" v-for="(advertencia, index) in detallesImportacion.advertencias_backend" :key="'backend-' + index">
                <b-icon icon="info-circle"></b-icon>
                {{ advertencia }}
              </b-alert>
            </div>
          </div>

          <!-- Si no hay advertencias -->
          <b-alert v-else show variant="light" class="text-center">
            <b-icon icon="check-circle" variant="success" font-scale="2"></b-icon>
            <p class="mt-2 mb-0">No se encontraron advertencias en esta importación</p>
          </b-alert>
        </b-tab>
      </b-tabs>

      <div class="d-flex justify-content-end mt-3">
        <b-button variant="secondary" @click="showDetallesModal = false">Cerrar</b-button>
      </div>
    </div>
  </b-modal>

  <!-- Modal de Confirmación de Reversión -->
  <b-modal v-model="showConfirmRevertModal" title="Confirmar Reversión de Importación" centered hide-footer>
    <div v-if="importacionARevertir">
      <b-alert show variant="danger" class="mb-3">
        <h5 class="mb-2">
          <b-icon icon="exclamation-triangle-fill"></b-icon>
          ¡ADVERTENCIA!
        </h5>
        <p class="mb-0">
          Esta acción eliminará TODOS los usuarios que fueron importados en este lote y <strong>NO SE PUEDE DESHACER</strong>.
        </p>
      </b-alert>

      <div class="mb-3">
        <h6>Detalles de la importación a revertir:</h6>
        <ul>
          <li><strong>ID:</strong> #{{ importacionARevertir.id }}</li>
          <li><strong>Fecha:</strong> {{ formatearFecha(importacionARevertir.created_at) }}</li>
          <li><strong>Usuario ejecutor:</strong> {{ importacionARevertir.usuario_ejecutor }}</li>
          <li><strong>Dominio:</strong> {{ importacionARevertir.escuela_filtrada ? importacionARevertir.escuela_filtrada.domain : 'General' }}</li>
          <li><strong>Total de usuarios a eliminar:</strong> <span class="text-danger font-weight-bold">{{ importacionARevertir.usuarios_creados || 0 }}</span></li>
        </ul>
      </div>

      <b-form-group label="Para confirmar, escribe 'REVERTIR' en mayúsculas:" label-for="confirm-text">
        <b-form-input
          id="confirm-text"
          v-model="textoConfirmacion"
          placeholder="Escribe REVERTIR..."
        />
      </b-form-group>

      <div class="d-flex justify-content-end mt-4">
        <b-button variant="secondary" @click="cancelarReversion">Cancelar</b-button>
        <b-button 
          variant="danger" 
          @click="confirmarReversion" 
          class="ml-2" 
          :disabled="textoConfirmacion !== 'REVERTIR' || revertiendoImportacion"
        >
          <b-spinner small v-if="revertiendoImportacion" class="mr-1" />
          <b-icon icon="trash" v-else class="mr-1"></b-icon>
          Sí, revertir y eliminar usuarios
        </b-button>
      </div>
    </div>
  </b-modal>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import toast from "@/mixins/ToastMixin.js";

export default {
  name: 'HistorialImportaciones',
  mixins: [toast],
  components: { vSelect },
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showModal: this.value,
      cargando: false,
      dominiosOptions: [],
      dominiosLoading: false,
      items: [],
      showDetallesModal: false,
      detallesImportacion: null,
      fields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'usuario_ejecutor', label: 'Usuario ejecutor', sortable: true },
        { key: 'dominio', label: 'Dominio', sortable: true },
        { key: 'created_at', label: 'Fecha', sortable: true },
        { key: 'exitoso', label: 'Estado', sortable: true },
        { key: 'total_procesados', label: 'Total', sortable: true },
        { key: 'resumen', label: 'Resumen', sortable: false },
        { key: 'estado_reversion', label: 'Reversión', sortable: false },
        { key: 'acciones', label: '', sortable: false }
      ],
      showConfirmRevertModal: false,
      importacionARevertir: null,
      revertiendoImportacion: false,
      textoConfirmacion: '',
      paginacion: {
        current_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0
      },
      filtros: {
        usuario: '',
        dominio: null,
        fecha_desde: '',
        fecha_hasta: '',
        exitoso: null
      },
      estadoOptions: [
        { value: null, text: 'Todos' },
        { value: 1, text: 'Exitosos' },
        { value: 0, text: 'Fallidos' },
        { value: 2, text: 'Parciales' }
      ]
    };
  },
  watch: {
    value(newVal) {
      this.showModal = newVal;
      if (newVal) {
        this.cargarDominios();
        this.limpiarFiltros();
        this.cargarHistorial();
      }
    },
    showModal(newVal) {
      this.$emit('input', newVal);
    }
  },
  methods: {
    async cargarDominios() {
      this.dominiosLoading = true;
      try {
        const response = await this.$api.get('/api/gestion-u/dominios-disponibles');
        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          // Agregar opción "General" al inicio
          this.dominiosOptions = [
            {
              value: 0,
              text: 'General',
              id: 0,
              domain: 'General',
              nombre: 'General'
            },
            ...response.data.data.map(d => ({ 
              value: d.id, 
              text: `${d.nombre} (${d.domain})`,
              id: d.id,
              domain: d.domain,
              nombre: d.nombre
            }))
          ];
        } else {
          this.dominiosOptions = [
            {
              value: 0,
              text: 'General',
              id: 0,
              domain: 'General',
              nombre: 'General'
            }
          ];
        }
      } catch (e) {
        this.dominiosOptions = [
          {
            value: 0,
            text: 'General',
            id: 0,
            domain: 'General',
            nombre: 'General'
          }
        ];
        this.toast('b-toaster-bottom-center', true, 'No se pudieron cargar los dominios', 'danger');
      } finally {
        this.dominiosLoading = false;
      }
    },
    formatearFecha(fecha) {
      if (!fecha) return '-';
      
      // Si viene en formato "23/10/2025 15:31:59" o "23\/10\/2025 15:31:59"
      if (typeof fecha === 'string' && fecha.includes('/')) {
        // Limpiar las barras escapadas si existen
        const fechaLimpia = fecha.replace(/\\/g, '');
        // Parsear manualmente el formato DD/MM/YYYY HH:mm:ss
        const partes = fechaLimpia.split(' ');
        const [dia, mes, anio] = partes[0].split('/');
        const hora = partes[1] || '00:00:00';
        // Crear fecha en formato ISO: YYYY-MM-DDTHH:mm:ss
        const fechaISO = `${anio}-${mes}-${dia}T${hora}`;
        const date = new Date(fechaISO);
        return date.toLocaleString('es-MX', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      // Si viene en otro formato, intentar parsearlo directamente
      const date = new Date(fecha);
      return date.toLocaleString('es-MX', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    async verDetalles(item) {
      try {
        // Cargar los detalles completos desde el backend
        const response = await this.$api.get(`/api/gestion-u/detalle-importacion/${item.id}`);
        
        if (response.data && response.data.success) {
          const data = response.data.data;
          
          // Mapear la estructura del backend a la estructura esperada por el componente
          this.detallesImportacion = {
            // Datos del resumen
            created_at: data.resumen.fecha_importacion,
            usuario_ejecutor: data.resumen.ejecutado_por,
            archivo_original: data.resumen.archivo_original,
            dominio_filtrado: data.resumen.dominio_filtrado,
            total_procesados: data.resumen.total_procesados,
            exitoso: data.resumen.exitoso,
            
            // Contadores
            usuarios_creados: data.estadisticas.usuarios_creados.cantidad,
            usuarios_actualizados: data.estadisticas.usuarios_actualizados.cantidad,
            usuarios_sin_cambios: data.estadisticas.usuarios_sin_cambios.cantidad,
            errores: data.estadisticas.usuarios_con_error.cantidad,
            
            // Detalles de usuarios
            detalles: {
              usuarios_creados: data.estadisticas.usuarios_creados.usuarios,
              usuarios_actualizados: data.estadisticas.usuarios_actualizados.usuarios,
              usuarios_sin_cambios: data.estadisticas.usuarios_sin_cambios.usuarios,
              usuarios_con_error: data.estadisticas.usuarios_con_error.errores
            },
            
            // Datos de reversión (si existen)
            revertida_at: data.resumen.revertida_at || null,
            revertida_por: data.resumen.revertida_por || null,
            total_revertidos: data.resumen.total_revertidos || 0,
            total_no_eliminados: data.resumen.total_no_eliminados || 0,
            detalles_no_eliminados: data.resumen.detalles_no_eliminados || null,
            
            // Advertencias
            advertencias_preview: data.advertencias_preview || null,
            advertencias_backend: data.advertencias_backend || []
          };
          
          this.showDetallesModal = true;
        } else {
          this.toast('b-toaster-bottom-center', true, 'No se pudieron cargar los detalles', 'danger');
        }
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message 
          ? e.response.data.message 
          : 'Error al cargar los detalles de la importación';
        this.toast('b-toaster-bottom-center', true, msg, 'danger');
      }
    },
    async cargarHistorial() {
      this.cargando = true;
      try {
        const params = {
          page: this.paginacion.current_page,
          per_page: this.paginacion.per_page
        };
        
        if (this.filtros.usuario) {
          params.usuario = this.filtros.usuario;
        }
        // Enviar el ID del dominio como escuela_id
        if (this.filtros.dominio) {
          // Si es un objeto, extraer el value que contiene el id
          if (typeof this.filtros.dominio === 'object') {
            params.escuela_id = this.filtros.dominio.value;
          } else {
            // Si es un número directo
            params.escuela_id = this.filtros.dominio;
          }
        }
        if (this.filtros.fecha_desde) {
          params.fecha_desde = this.filtros.fecha_desde;
        }
        if (this.filtros.fecha_hasta) {
          params.fecha_hasta = this.filtros.fecha_hasta;
        }
        // Enviar el estado como 0, 1 o 2
        if (this.filtros.exitoso !== null && this.filtros.exitoso !== '') {
          params.exitoso = this.filtros.exitoso;
        }

        const response = await this.$api.get('/api/gestion-u/historial-importaciones', { params });
        
        if (response.data && response.data.success) {
          this.items = response.data.data.data || [];
          this.paginacion.current_page = response.data.data.current_page;
          this.paginacion.per_page = response.data.data.per_page;
          this.paginacion.total = response.data.data.total;
          this.paginacion.from = response.data.data.from;
          this.paginacion.to = response.data.data.to;
        } else {
          this.items = [];
          this.toast('b-toaster-bottom-center', true, 'No se pudo cargar el historial', 'danger');
        }
      } catch (e) {
        this.items = [];
        const msg = e.response && e.response.data && e.response.data.message 
          ? e.response.data.message 
          : 'Error al cargar el historial de importaciones';
        this.toast('b-toaster-bottom-center', true, msg, 'danger');
      } finally {
        this.cargando = false;
      }
    },
    limpiarFiltros() {
      this.filtros = {
        usuario: '',
        dominio: null,
        fecha_desde: '',
        fecha_hasta: '',
        exitoso: null
      };
      this.paginacion.current_page = 1;
      this.cargarHistorial();
    },
    mostrarConfirmReversion(importacion) {
      this.importacionARevertir = importacion;
      this.textoConfirmacion = '';
      this.showConfirmRevertModal = true;
    },
    cancelarReversion() {
      this.showConfirmRevertModal = false;
      this.importacionARevertir = null;
      this.textoConfirmacion = '';
    },
    async confirmarReversion() {
      if (this.textoConfirmacion !== 'REVERTIR') {
        this.toast('b-toaster-bottom-center', true, 'Debes escribir REVERTIR para confirmar', 'warning');
        return;
      }

      this.revertiendoImportacion = true;
      
      try {
        const response = await this.$api.post(`/api/gestion-u/revertir-importacion/${this.importacionARevertir.id}`);
        
        if (response.data && response.data.success) {
          const resultado = response.data.data;
          
          // Mensaje de éxito con detalles
          let mensaje = `Reversión completada: ${resultado.total_eliminados} usuarios eliminados`;
          
          if (resultado.total_no_eliminados > 0) {
            mensaje += ` (${resultado.total_no_eliminados} no pudieron eliminarse)`;
          }
          
          this.toast('b-toaster-bottom-center', true, mensaje, response.data.color || 'success');
          
          // Cerrar modal y recargar historial
          this.showConfirmRevertModal = false;
          this.importacionARevertir = null;
          this.textoConfirmacion = '';
          await this.cargarHistorial();
        } else {
          this.toast('b-toaster-bottom-center', true, response.data.message || 'Error al revertir importación', 'danger');
        }
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message 
          ? e.response.data.message 
          : 'Error al revertir la importación';
        this.toast('b-toaster-bottom-center', true, msg, 'danger');
      } finally {
        this.revertiendoImportacion = false;
      }
    },
    getUsuariosEliminados() {
      if (!this.detallesImportacion || !this.detallesImportacion.detalles) {
        return [];
      }

      // Si no hay reversión, retornar array vacío
      if (!this.detallesImportacion.revertida_at) {
        return [];
      }

      // Intentar obtener la lista de usuarios creados originalmente
      const usuariosCreados = this.detallesImportacion.detalles.usuarios_creados || [];
      
      // Obtener la lista de usuarios NO eliminados de detalles_no_eliminados
      let usuariosNoEliminados = [];
      if (this.detallesImportacion.detalles_no_eliminados) {
        try {
          // Si viene como string JSON, parsearlo
          if (typeof this.detallesImportacion.detalles_no_eliminados === 'string') {
            const parsed = JSON.parse(this.detallesImportacion.detalles_no_eliminados);
            usuariosNoEliminados = parsed.usuarios_no_eliminados || parsed || [];
          } else if (Array.isArray(this.detallesImportacion.detalles_no_eliminados)) {
            // Si ya es un array
            usuariosNoEliminados = this.detallesImportacion.detalles_no_eliminados;
          } else if (this.detallesImportacion.detalles_no_eliminados.usuarios_no_eliminados) {
            // Si es un objeto con usuarios_no_eliminados
            usuariosNoEliminados = this.detallesImportacion.detalles_no_eliminados.usuarios_no_eliminados;
          }
        } catch (e) {
          console.error('Error parseando detalles_no_eliminados:', e);
        }
      }

      // Filtrar los usuarios creados que NO están en la lista de no eliminados
      const emailsNoEliminados = usuariosNoEliminados.map(u => u.email);
      const usuariosEliminados = usuariosCreados.filter(u => !emailsNoEliminados.includes(u.email));

      return usuariosEliminados;
    },
    getUsuariosNoEliminados() {
      if (!this.detallesImportacion || !this.detallesImportacion.revertida_at) {
        return [];
      }

      // Obtener los detalles de usuarios no eliminados
      if (!this.detallesImportacion.detalles_no_eliminados) {
        return [];
      }

      try {
        // Si viene como string JSON, parsearlo
        if (typeof this.detallesImportacion.detalles_no_eliminados === 'string') {
          const parsed = JSON.parse(this.detallesImportacion.detalles_no_eliminados);
          return parsed.usuarios_no_eliminados || parsed || [];
        } else if (Array.isArray(this.detallesImportacion.detalles_no_eliminados)) {
          // Si ya es un array
          return this.detallesImportacion.detalles_no_eliminados;
        } else if (this.detallesImportacion.detalles_no_eliminados.usuarios_no_eliminados) {
          // Si es un objeto con usuarios_no_eliminados
          return this.detallesImportacion.detalles_no_eliminados.usuarios_no_eliminados;
        }
      } catch (e) {
        console.error('Error parseando detalles_no_eliminados:', e);
        return [];
      }

      return [];
    },
    tieneAdvertencias() {
      if (!this.detallesImportacion) {
        return false;
      }

      // Verificar advertencias del preview
      if (this.detallesImportacion.advertencias_preview) {
        const preview = this.detallesImportacion.advertencias_preview;
        
        // Verificar si hay advertencias "otras"
        if (preview.otras && preview.otras.length > 0) {
          return true;
        }
        
        // Verificar emails inválidos
        if (preview.emails_invalidos && preview.emails_invalidos.total > 0) {
          return true;
        }
        
        // Verificar dominios no encontrados
        if (preview.dominios_no_encontrados && preview.dominios_no_encontrados.total > 0) {
          return true;
        }
      }

      // Verificar advertencias del backend
      if (this.detallesImportacion.advertencias_backend && this.detallesImportacion.advertencias_backend.length > 0) {
        return true;
      }

      return false;
    },
    cerrar() {
      this.showModal = false;
    },
    onClose() {
      this.$emit('input', false);
    }
  }
};
</script>

<style scoped>
</style>
